import { z } from 'zod';

export type UiVersion = 'app-v2' | 'app-v3';

export const UI_VERSION_QUERY_KEY = 'ui';
export const UI_VERSION_STORAGE_KEY = 'braise:ui-version';

const uiVersionSchema = z.enum(['app-v2', 'app-v3']);

const SHORTHAND_MAP: Record<string, UiVersion> = {
  'v2': 'app-v2',
  'app-v2': 'app-v2',
  'legacy': 'app-v2',
  'classic': 'app-v2',
  'v3': 'app-v3',
  'app-v3': 'app-v3',
  'next': 'app-v3',
};

export const normalizeUiVersion = (raw: string | null | undefined): UiVersion | null => {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if (!value) return null;
  const mapped = SHORTHAND_MAP[value];
  if (mapped) return mapped;
  const parsed = uiVersionSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
};

export const getEnvUiVersion = (): UiVersion => {
  const envValue =
    (import.meta.env?.VITE_UI_VERSION as string | undefined) ??
    (import.meta.env?.VITE_APP_SHELL as string | undefined) ??
    'app-v2';
  return normalizeUiVersion(envValue) ?? 'app-v2';
};

export const getStoredUiVersion = (): UiVersion | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(UI_VERSION_STORAGE_KEY);
    return normalizeUiVersion(raw);
  } catch {
    return null;
  }
};

export const setStoredUiVersion = (value: UiVersion | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (value) {
      window.sessionStorage.setItem(UI_VERSION_STORAGE_KEY, value);
    } else {
      window.sessionStorage.removeItem(UI_VERSION_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
};

export const formatUiVersionLabel = (version: UiVersion): string =>
  version === 'app-v3' ? 'UI v3' : 'UI v2';

export const getShortUiVersion = (version: UiVersion): string =>
  version === 'app-v3' ? 'v3' : 'v2';

declare global {
  interface Window {
    __BRAISE_UI_VERSION__?: UiVersion;
  }
}

export const getActiveUiVersion = (): UiVersion => {
  if (typeof window !== 'undefined' && window.__BRAISE_UI_VERSION__) {
    return window.__BRAISE_UI_VERSION__;
  }
  return getEnvUiVersion();
};

export const setActiveUiVersion = (version: UiVersion) => {
  if (typeof window !== 'undefined') {
    window.__BRAISE_UI_VERSION__ = version;
    try {
      document.documentElement.dataset.uiVersion = version;
    } catch {
      // ignore DOM access issues
    }
  }
};
